import React, { useState } from 'react'
import GenericModal from '../../../../components/GenericModal'
import Dropdown from '../../../../components/Dropdown'
import CsvMappingUpload from '../../../../components/CsvMappingUpload'
import { useAppDispatch } from '../../../../store'
import { getEnrollmentsApi, getTermsSectionsApi } from '../../../../store/slices/termsSectionsSlice/api'
import { CreateEnrollment } from '../CreateEnrollment'
import { TableModalAction, type ApplyFiltersAndDispatchParams } from '../../../../components/CustomTable/types'
import { useParams } from 'react-router-dom'
import { getTerm } from '../../../../store/slices/termsSlice/selectors'
import { notification } from '../../../../components/Notifications'
import { useMutation } from '@tanstack/react-query'
import { resetEnrollmentsByTermId } from '../../../../APIManager/UserManager'
import { type ApiError } from '../../../../models/Common'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { grey } from '../../../../colors'
import { getEnrollmentList } from '../../../../store/slices/termsSectionsSlice/selectors'
interface TopbarActionsProps {
  params?: ApplyFiltersAndDispatchParams
  termId: string
  allowedActions?: string[]
  handleExport: () => void
  setUploadTaskId: (id: string | null) => void
  isTaskRunning: boolean
  onTaskBlock: () => void
}

export const TopbarActions: React.FC<TopbarActionsProps> = ({ params, termId, allowedActions, handleExport, setUploadTaskId, isTaskRunning, onTaskBlock }) => {
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false)
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)
  const [showResetConfirmationModal, setShowResetConfirmationModal] = useState(false)
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const term = getTerm()

  const closeUploadModal = (): void => {
    setIsUploadModal(false)
  }

  const uploadApi = (): void => {
    if (isTaskRunning) { onTaskBlock(); return }
    setIsUploadModal(true)
  }

  const addManually = (): void => {
    if (isTaskRunning) { onTaskBlock(); return }
    setIsCreateModal(true)
  }

  const menuItems = [
    { text: 'Upload Enrollments (.csv)', action: uploadApi },
    { text: 'Add Manually', action: addManually }
  ]
  const resetTooltip = 'Delete enrollments of course sections that do not have a survey in published, live or closed state. This action cannot be undone.'
  const resetModalText = 'Deletes enrollments of course sections that do not have a survey in published, live or closed state. This action cannot be undone.'
  const { mutate: resetEnrollments, isPending: isResetting } = useMutation({
    mutationFn: async () => {
      return await resetEnrollmentsByTermId(termId ?? '')
    },
    onSuccess: (response) => {
      notification(response?.data?.message, { variant: 'success' })
      void dispatch(getEnrollmentsApi({ urlId: id, params }))
      void dispatch(getTermsSectionsApi({ params: { ...params, termId: id } }))
      setShowResetConfirmationModal(false)
    },
    onError: (error: ApiError) => {
      notification(error.message || 'Failed to reset enrollments.', { variant: 'error' })
    }
  })

  const resetMenuItems = [
    {
      text: 'Export Enrollments (.csv)',
      action: () => { handleExport() }
    },
    {
      text: 'Reset Enrollments',
      action: () => { setShowResetConfirmationModal(true) },
      disabled: isTaskRunning,
      tooltipTitle: resetTooltip
    }
  ]

  const enrollmentsList = getEnrollmentList()
  const isEnrollmentsEmpty = !enrollmentsList?.data || enrollmentsList.data.length === 0

  return (
    <>
      {allowedActions?.includes(TableModalAction.CREATE) && <Dropdown menuItems={menuItems} label='Add Enrollments' />}
      <GenericModal
        isOpen={isCreateModal}
        title={'Create Enrollment'}
        onClose={() => { setIsCreateModal(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateEnrollment params={params} handleCloseModal={() => { setIsCreateModal(false) }} />
      </GenericModal>

      <CsvMappingUpload isOpen={isUploadModal} onClose={closeUploadModal} modalTitle="Enrollments" csvHeaderEndpoint="/api/users/roles/csv_headers?is_student=true" uploadCsvEndpoint={`/api/users/roles/?is_student=True&term_id=${termId ?? id}`} hasCreatePermission={allowedActions?.includes(TableModalAction.CREATE)} returnTaskId={(taskId) => { if (taskId) setUploadTaskId(taskId) }} onSuccess={() => {
        notification('Enrollments successfully uploaded', { variant: 'success' })
        void dispatch(getEnrollmentsApi({ urlId: id, params }))
      }} />
      {allowedActions?.includes(TableModalAction.DELETE) && (
        <>
          <Dropdown
            menuItems={resetMenuItems}
            dropdownType='action'
            extraButtonProps={{
              style: { p: 0 },
              disabled: isResetting || isEnrollmentsEmpty,
              circularProgressStyles: { color: grey[300] },
              buttonType: ButtonType.Ghost
            }}
            disabled={isEnrollmentsEmpty}

          />
          <GenericModal
            isOpen={showResetConfirmationModal}
            onClose={() => { setShowResetConfirmationModal(false) }}
            title={'Reset Enrollments'}
            maxWidth='sm'
            onConfirm={async () => { resetEnrollments() }}
            confirmButtonLoading={isResetting}
          >
            {resetModalText}
          </GenericModal>
        </>
      )}
    </>
  )
}
